import { getCookie } from '@/router/index.js';

export default {
  name: 'ResetPasswordSent',

  props: {
    uidb64: { type: String, required: false },
    token: { type: String, required: false },
  },

  data: () => ({
    verifying: true,
    link_valid: false,
    submitting: false,

    new_password: '',
    confirm_password: '',

    errors: { new_password: '', confirm_password: '' },
    email:
      (typeof window !== 'undefined' &&
        window.sessionStorage.getItem('reset_email')) ||
      '',
    meets_criteria: {
      length: false,
      lowercase: false,
      uppercase: false,
      special: false,
    },
    password_visible: false,
    confirm_password_visible: false,

    csrfToken: null,
  }),

  computed: {
    uid() { return this.uidb64 || this.$route.params.uidb64 || ''; },
    tok() { return this.token || this.$route.params.token || ''; },
  },

  watch: {
    new_password(val) {
      this.check_password_criteria(val);
    },
  },

  mounted() {
    this.csrfToken = getCookie('csrftoken') || null;
    this.ensure_csrf().then(() => {
      this.verify_link();
    });
  },

  methods: {
    base() {
      return (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '');
    },

    toggle_password_visibility(which) {
      if (which === 'password') this.password_visible = !this.password_visible;
      if (which === 'confirm') this.confirm_password_visible = !this.confirm_password_visible;
    },

    check_password_criteria(password) {
      this.meets_criteria = {
        length: password.length >= 8,
        lowercase: /[a-z]/.test(password),
        uppercase: /[A-Z]/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      };
    },

    validate_form() {
      let valid = true;

      if (!this.new_password) {
        this.$notyf.error('Password must be at least 8 characters.');
        valid = false;
      } else if (!this.confirm_password) {
        this.$notyf.error('Please confirm your new password.');
        valid = false;
      } else if (this.confirm_password !== this.new_password) {
        this.$notyf.error('New and confirm passwords do not match.');
        valid = false;
      } else if (!Object.values(this.meets_criteria).every(Boolean)) {
        this.$notyf.error('Password does not meet required criteria.');
        valid = false;
      }
      return valid;
    },

    async ensure_csrf() {
      if (!getCookie('csrftoken')) {
        const url = this.base() + '/aic_one_api/system_management/get_csrf_token/';
        try {
          await fetch(url, { method: 'GET', credentials: 'include' });
        } catch (e) {
          this.csrfToken = getCookie('csrftoken') || this.csrfToken;
        }
      }
      this.csrfToken = getCookie('csrftoken') || this.csrfToken;
    },

    async verify_link() {
      this.verifying = true;
      this.link_valid = false;

      const url =
        this.base() + '/aic_one_api/system_management/verify_reset_link/' +
        '?uidb64=' + encodeURIComponent(this.uid) +
        '&token=' + encodeURIComponent(this.tok);

      try {
        await this.ensure_csrf();

        const headers = {};
        if (this.csrfToken) headers['X-CSRFToken'] = this.csrfToken;

        const res = await fetch(url, { method: 'GET', credentials: 'include', headers: headers });
        const data = await res.json().catch(function () { return {}; });

        const ok = (data && (data.status === 'success' || data.valid === true)) ? true : false;
        this.link_valid = !!ok;

        if (!this.link_valid) {
          const msg = (data && data.message) ? data.message : 'Invalid or expired link.';
          this.$notyf.info(msg);
        }
      } catch (e) {
        this.link_valid = false;
        this.$notyf.error('Invalid or expired link.');
      } finally {
        this.verifying = false;
      }
    },

    async submit_reset() {
      if (!this.validate_form()) return;

      this.submitting = true;

      const url = this.base() + '/aic_one_api/system_management/forgot_password_reset/';
      const payload = {
        uidb64: this.uid,
        token: this.tok,
        new_password: this.new_password,
        confirm_password: this.confirm_password,
      };

      try {
        await this.ensure_csrf();

        const res = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.csrfToken || '',
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json().catch(function () { return {}; });

        if (data && data.status === 'success') {
          this.$notyf.success('Password reset successfully.');
          this.$router.push('/');
          return;
        }

        const field_errors = (data && data.errors) ? data.errors : {};
        if (Array.isArray(field_errors.new_password) && field_errors.new_password.length) {
          this.errors.new_password = field_errors.new_password.join(', ');
        }
        if (Array.isArray(field_errors.confirm_password) && field_errors.confirm_password.length) {
          this.errors.confirm_password = field_errors.confirm_password.join(', ');
        }

        const msg = (data && data.message) ? data.message : 'Reset failed.';
        this.$notyf.error(msg);
      } catch (e) {
        this.$notyf.error('Reset failed.');
      } finally {
        this.submitting = false;
      }
    },
  },
};
